import {
  CaretDown,
  CaretUp,
  Check,
  Eye,
  PaperPlaneRight,
  PencilSimple,
  X,
} from 'phosphor-react';
import { useNavigate } from 'react-router-dom';
import React, { useState } from 'react';
import { maskPhone } from '../../../../services/helpers/mask';
import { Button } from '../../../../shared/Button';
import { Table } from '../../../../shared/Table';
import { dateOffset, formatDate } from '../../../../utils/dateFunctions';
import styles from './budget-list.module.css';
import { COLORS } from '../../../../styles/colors';

import { ListLoader } from '../../../../shared/ListLoader';
import { Budget } from '../../../../@types/budget';
import { FeedbackModal } from '../../../../shared/FeedbackModal';
import { useResponsive } from '../../../../hooks/useResponsive';
import { useAuth } from '../../../../hooks/useAuth';
import useSelectedBudget from './hooks/useSelectedBudget';
import useRemoveModal from './hooks/useRemoveModal';
import { OrderServiceFormDTO, ServiceDTO } from '../../OrderService/CreateOrderServiceForm/OrderServiceDTO';
import { defaultOS } from '../../OrderService/CreateOrderServiceForm/createOSMockup';
import { parseOSForm } from '../../OrderService/CreateOrderServiceForm/parseOrderServiceForm';
import { createRepairDraftController } from '../../OrderService/controller';
import { handleWhatsappMessage } from '../CreateBudgetForm/handleWhatsappMessage';
import { transformBudgetDraftController } from '../EditBudgetForm/controller';
import { getBudgetByIdController } from '../controller';

interface Props {
  budgets: Budget[];
  onScroll?: () => void;
  isLoading: boolean;
  height: number;
}

export const BudgetTable = ({
  budgets,
  onScroll,
  isLoading,
  height,
}: Props) => {
  const navigate = useNavigate();
  const removeModal = useRemoveModal();
  const budgetSelection = useSelectedBudget();
  const [isTransforming, setIsTransforming] = useState(false);

  // const tableRef = useInfiniteScroll(onScroll);
  const { workshop } = useAuth();

  const formatRepairData = (budget: Budget) => {
    const budgetDate = dateOffset(new Date(budget.created_at));

    return {
      budgetCode: budget?.BUDGET ?? '---',
      name: budget.name ?? '---',
      phone: maskPhone(budget.phone ?? '---'),
      vehiclePlate: budget?.plate?.length > 0 ? budget?.plate : '---',
      vehicleName: budget?.vehicle?.length > 0 ? budget?.vehicle : '---',
      formattedDate: formatDate(budgetDate),
    };
  };

  const handleEditBudget = async (budget: Budget) => {
    try {
      setIsTransforming(true);

      if (budget.draft) {
        await transformBudgetDraftController(budget.id_budget);
      }

      navigate(`/edit-budget-form?budgetId=${budget.id_budget}`);
    } catch (error) {
      console.error(error);
    } finally {
      setIsTransforming(false);
    }
  };

  const handleCreateOS = async (budget: Budget) => {
    try {
      setIsTransforming(true);

      const completeBudget = await getBudgetByIdController(budget.id_budget);

      if (!completeBudget) {
        console.error('Failed to fetch complete budget data');
        setIsTransforming(false);
        return;
      }

      // Separate products and services
      const productTable: ServiceDTO[] = [];
      const serviceTable: ServiceDTO[] = [];

      completeBudget.child_services.forEach(item => {
        const quantity = Number(item.quantity ?? 0);
        const total = Number(item.total ?? 0);
        const unitValue = (quantity > 0 ? total / quantity : 0)
          .toFixed(2)
          .replace('.', ',');

        const serviceItem: ServiceDTO = {
          name: item.name,
          quantity: `${quantity}`,
          unitValue,
          total: total.toFixed(2).replace('.', ','),
        };

        if (item.type === 'Serviço') {
          serviceTable.push(serviceItem);
        } else {
          productTable.push(serviceItem);
        }
      });

      // Detect if it's a walk-in client (no registered user)
      const isNoClient = !completeBudget?.user_id || completeBudget?.user_id.trim() === '';

      // Detect if it's an OS without vehicle (based on is_plate flag)
      const isNoVehicle = completeBudget?.is_plate === false;

      const newOSObject: OrderServiceFormDTO = {
        ...defaultOS,
        customer: {
          ...defaultOS.customer,
          document: completeBudget?.user?.document ?? completeBudget?.document ?? '',
          name: completeBudget?.user?.name ?? completeBudget?.name ?? '',
          phone: completeBudget?.user?.phone ?? completeBudget?.phone ?? '',
          noClient: isNoClient,
          userId: completeBudget?.user_id ?? '',
        },
        services: {
          ...defaultOS.services,
          comment: completeBudget?.comment ?? '',
          discount: `${(completeBudget?.discount ?? 0).toFixed(2).replace('.', ',')}`,
          productTable,
          serviceTable,
          images:
            completeBudget?.checklist?.banners?.map(banner => ({
              category: '',
              img_url: banner.img_url ?? '',
            })) ?? [],
        },
        vehicle: {
          ...defaultOS.vehicle,
          plate: completeBudget?.plate ?? '',
          name: completeBudget?.vehicle ?? '',
          km: completeBudget?.km ? `${completeBudget.km}` : '',
          lastKm: completeBudget?.km ? `${completeBudget.km}` : '',
          noVehicle: isNoVehicle,
        },
      };

      // console.log('newOSObject', newOSObject);
      const parsedForm = parseOSForm(newOSObject);
      // console.log('parsedForm', parsedForm);
      const res = await createRepairDraftController(parsedForm);

      if (res) {
        navigate(`/new-order-service-form?repairId=${res.repair.id_repair}`);
      }
    } catch (error) {
      console.error('Error converting budget to OS:', error);
    }
  };

  const { sizeMobile: isMobile } = useResponsive();

  const renderActionButtons = (budget: Budget) => (
    <div className={styles['table-actions']}>
      <div className={styles['action-buttons']}>
        <div className={styles['action-scroll']}>
          <Button
            handleClick={() => handleEditBudget(budget)}
            disabled={isTransforming}
          >
            <PencilSimple size={20} color={COLORS.WHITE} />
            {isTransforming && budget.draft ? 'Finalizando...' : 'Editar'}
          </Button>

          <Button
            handleClick={() =>
              navigate(`/budget-report?budgetId=${budget?.id_budget ?? ''}`)
            }
          >
            <Eye size={20} color={COLORS.WHITE} />
            Visualizar
          </Button>

          <Button handleClick={removeModal.onOpen}>
            <X size={20} color={COLORS.WHITE} weight="bold" />
            Excluir
          </Button>

          <Button handleClick={() => handleCreateOS(budget)}>
            <Check size="1.2rem" weight="bold" />
            Iniciar OS
          </Button>

          <Button
            handleClick={() =>
              handleWhatsappMessage(budget, workshop?.fantasy_name ?? '')
            }
          >
            <PaperPlaneRight size="1.2rem" />
            Enviar
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <div className={styles['table-container']} style={{ height }}>
      <FeedbackModal isOpen={isTransforming} variant="sending" />
      <Table.Root
        // ref={tableRef}
        className={styles.table}
        tableScrollClassName={styles['table-scroll']}
      >
        <Table.Row className={styles['table-header']}>
          <Table.Header value={isMobile ? 'N.' : 'Número'} />
          <Table.Header value="Cliente" />
          <Table.Header value="Placa" />
          <Table.Header value="Veículo" className={styles.desktop} />
          <Table.Header value="Contato" className={styles.desktop} />
          <Table.Header value="Data" />
        </Table.Row>
        {budgets.map(budget => {
          const {
            budgetCode,
            name,
            phone,
            vehiclePlate,
            vehicleName,
            formattedDate,
          } = formatRepairData(budget);

          const isSelected =
            budgetSelection.selectedBudget?.id_budget === budget.id_budget;
          const RowIcon = isSelected ? CaretUp : CaretDown;
          return (
            <React.Fragment key={budget.id_budget}>
              <Table.Row
                className={`${styles['table-row']} ${isSelected ? styles.selected : ''
                  }`}
                onClick={() =>
                  budgetSelection.selectedBudget?.id_budget === budget.id_budget
                    ? budgetSelection.onRemove()
                    : budgetSelection.onSelect(budget)
                }
              >
                <Table.Cell>
                  <div>
                    <div className={styles['first-column']}>
                      {!isMobile && <RowIcon size={20} />}
                      {budgetCode}
                    </div>
                  </div>
                </Table.Cell>
                <Table.Cell className={styles.name}>{name}</Table.Cell>
                <Table.Cell>{vehiclePlate}</Table.Cell>
                <Table.Cell className={styles.desktop}>
                  {vehicleName}
                </Table.Cell>
                <Table.Cell className={styles.desktop}>{phone}</Table.Cell>
                <Table.Cell className={styles.date}>{formattedDate}</Table.Cell>
              </Table.Row>
              {budgetSelection.selectedBudget?.id_budget === budget.id_budget &&
                renderActionButtons(budget)}
            </React.Fragment>
          );
        })}
      </Table.Root>
      {isLoading && <ListLoader />}
    </div>
  );
};
